// Google Business Profile reads for the AI Visibility dashboard (SGL-328 Phase A).
//
// Both endpoints live on social-management's token_manager (gbp_audit_handler /
// gbp_performance_handler, shaped by token_manager/gbp_profile.py) and reuse the
// GMB connection made for publishing, so there is no separate "connect GBP for
// visibility" flow. They are READ-ONLY by contract (AC5): nothing here, or on
// the backend path behind it, edits the profile.
//
// Honesty rules the types below encode, and the UI must keep:
//   - A section that failed is {status: "error", reason}, never zeros. "We could
//     not read your reviews" is not the same fact as "you have no reviews".
//   - Q&A is always "unavailable" (Google shut its API down on 2025-11-03). It
//     must never be shown as "0 questions".
//   - A keyword `threshold` means "fewer than N" (Google hides small counts). It
//     is not a count: never display it as one, never add it to anything.
//   - `totals` is null (not zeros) when the performance metrics failed.
import { authedFetch } from "../auth/authedFetch";
import { getApiBaseUrl } from "../utils/urlHandler";
import { GBP_PHOTO_CATEGORIES } from "./gbpConstants";
import type { GbpPhotoCategory } from "./gbpConstants";

/* ── shared section shapes (gbp_profile._run_section) ──────────────────── */

/** gbp_profile._classify_http_error: 401/403/404/429 map to a code, anything
 *  else (5xx, timeout, unparseable body, unexpected shape) is fetch_failed. */
export type GbpSectionErrorReason =
  | "needs_reauth"
  | "forbidden"
  | "not_found"
  | "rate_limited"
  | "fetch_failed";

export interface GbpSectionError {
  status: "error";
  reason: GbpSectionErrorReason;
  /** Only ever set on a 403 where Google says the API is not enabled on OUR
   *  GCP project — a Sagelyn setup problem, not the customer's. */
  detail?: "api_not_enabled";
}

/** A section loaded; its data fields sit alongside the status. */
export type GbpSection<T> = ({ status: "ok" } & T) | GbpSectionError;

/* ── audit: GET /{customer_id}/gbp/audit ───────────────────────────────── */

export interface GbpCategory {
  /** Google's category resource name, e.g. "categories/gcid:dentist". */
  id: string | null;
  display_name: string | null;
}

export interface GbpRegularHoursPeriod {
  /** Google DayOfWeek, e.g. "MONDAY". */
  open_day: string | null;
  /** "HH:MM" */
  open_time: string | null;
  close_day: string | null;
  close_time: string | null;
}

export interface GbpServiceArea {
  /** Google BusinessType, e.g. "CUSTOMER_AND_BUSINESS_LOCATION". */
  business_type: string | null;
  places: string[];
  region_code: string | null;
}

export interface GbpBusinessInfo {
  title: string | null;
  primary_category: GbpCategory | null;
  additional_categories: GbpCategory[];
  description: string;
  description_length: number;
  description_max_length: number;
  /** Human-readable names (structured items resolved via category serviceTypes
   *  where Google provides them, otherwise the raw serviceTypeId). */
  service_items: string[];
  /** false = Google offers no service list for this category; null = Google
   *  did not say. */
  can_modify_service_list: boolean | null;
  service_area: GbpServiceArea | null;
  has_storefront_address: boolean;
  regular_hours: GbpRegularHoursPeriod[];
  special_hours_count: number;
  phone: string | null;
  website_uri: string | null;
  /** Google OpenInfo.status, e.g. "OPEN". */
  open_status: string | null;
  /** "YYYY-MM-DD", "YYYY-MM" or "YYYY" — Google's day and month are optional. */
  opening_date: string | null;
  labels: string[];
  /** Google applied its own edits the owner has not reviewed. */
  has_google_updated: boolean;
  /** Owner edits are waiting on Google's review. */
  has_pending_edits: boolean;
  place_id: string | null;
  maps_uri: string | null;
}

export interface GbpAttributes {
  count: number;
  names: string[];
}

export interface GbpPhotos {
  /** Google's own total (all media, not just photos), independent of paging. */
  total_media_count: number;
  items_scanned: number;
  /** Photos among the SCANNED items — a lower bound when `truncated`. */
  photo_count: number;
  video_count: number;
  /** Newest scanned media item of any format (photo or video), RFC 3339. */
  newest_create_time: string | null;
  truncated: boolean;
}

export interface GbpPosts {
  count_seen: number;
  live_count: number;
  /** RECURRING posts are on the profile right now even when old. */
  recurring_count: number;
  latest_live_create_time: string | null;
  truncated: boolean;
}

export interface GbpReviews {
  total_count: number;
  average_rating: number | null;
  /** The latest N (by update time) that reply figures are computed over. */
  reviews_scanned: number;
  replied_count: number;
  unreplied_count: number;
  /** 0–1 over `reviews_scanned`, NOT `total_count`; null when nothing scanned. */
  reply_rate: number | null;
  newest_review_time: string | null;
  truncated: boolean;
}

/** Q&A is never fetched: Google discontinued the API. The backend always
 *  sends exactly this shape today. */
export interface GbpQandaUnavailable {
  status: "unavailable";
  reason: "google_discontinued_qanda_api";
  detail: string;
}

/** gbp_profile.compute_gaps. `code` is typed as string because the backend
 *  owns the list; the UI must degrade gracefully on a code it does not know. */
export interface GbpGap {
  code: string;
  /** The Google field path, e.g. "profile.description". */
  field: string;
  detail: string;
}

/** Codes compute_gaps emits today (for the UI's title map only). */
export type KnownGbpGapCode =
  | "no_additional_categories"
  | "empty_description"
  | "no_service_items"
  | "no_service_area"
  | "no_opening_date"
  | "no_website"
  | "no_phone"
  | "no_regular_hours"
  | "no_reviews"
  | "reviews_without_reply"
  | "no_questions"
  | "unanswered_questions"
  | "no_posts"
  | "stale_posts"
  | "no_photos";

/**
 * Location-level statuses.
 *
 * From the per-location token step (app._gbp_location_token), sent with NO
 * section data:
 *   needs_reauth  — the stored grant was revoked/expired; only a reconnect fixes it
 *   not_connected — no stored grant, or a malformed stored connection
 *   fetch_failed  — transient (network / AWS / unexpected token response)
 *
 * From gbp_profile.rollup_status, sent WITH section data:
 *   ok / partial / error, or needs_reauth when any section got a 401.
 */
export type GbpTokenStatus = "needs_reauth" | "not_connected" | "fetch_failed";
export type GbpRollupStatus = "ok" | "partial" | "error" | "needs_reauth";

interface GbpLocationBase {
  location_id: string;
  /** null when the stored connection has no account id (then not_connected). */
  account_id: string | null;
}

/** A location we could not even get a token for — no section data. */
export interface GbpUnreadableLocation extends GbpLocationBase {
  status: GbpTokenStatus;
}

export interface GbpAuditLocation extends GbpLocationBase {
  status: GbpRollupStatus;
  /** null when business_info failed. */
  title: string | null;
  business_info: GbpSection<GbpBusinessInfo>;
  attributes: GbpSection<GbpAttributes>;
  photos: GbpSection<GbpPhotos>;
  posts: GbpSection<GbpPosts>;
  reviews: GbpSection<GbpReviews>;
  qanda: GbpQandaUnavailable;
  gaps: GbpGap[];
}

export interface GbpAuditResponse {
  /** false = no GMB connection at all (a normal state, not an error). */
  connected: boolean;
  locations: (GbpAuditLocation | GbpUnreadableLocation)[];
}

/* ── performance: GET /{customer_id}/gbp/performance?months=N ─────────── */

export interface GbpMetricTotals {
  impressions_maps: number;
  impressions_search: number;
  /** desktop + mobile == maps + search (each impression lands in both). */
  impressions_desktop: number;
  impressions_mobile: number;
  calls: number;
  direction_requests: number;
  website_clicks: number;
}

export interface GbpMonthlyRow extends GbpMetricTotals {
  /** "YYYY-MM" */
  month: string;
}

/** Exactly one of value/threshold is non-null. */
export interface GbpKeyword {
  keyword: string | null;
  /** Exact impression count (0 when Google omitted the int64 zero). */
  value: number | null;
  /** "Fewer than this many" — Google hides exact small counts. */
  threshold: number | null;
}

export interface GbpKeywords {
  /** Exact counts first (largest first), then thresholds (largest first). */
  items: GbpKeyword[];
  truncated: boolean;
}

export interface GbpPerformanceLocation extends GbpLocationBase {
  status: GbpRollupStatus;
  /** [] when metrics failed. */
  monthly: GbpMonthlyRow[];
  /** null, not zeros, when metrics failed. */
  totals: GbpMetricTotals | null;
  keywords: GbpSection<GbpKeywords>;
  /** Present only when metrics failed: the section error minus its status. */
  metrics_error?: Omit<GbpSectionError, "status">;
}

export interface GbpRange {
  /** "YYYY-MM", inclusive — the last `months` COMPLETE calendar months. */
  start_month: string;
  end_month: string;
  /** Clamped server-side to 1–12. */
  months: number;
}

export interface GbpPerformanceResponse {
  connected: boolean;
  range: GbpRange;
  locations: (GbpPerformanceLocation | GbpUnreadableLocation)[];
}

/* ── narrowing helpers ─────────────────────────────────────────────────── */

export function isAuditedLocation(
  loc: GbpAuditLocation | GbpUnreadableLocation,
): loc is GbpAuditLocation {
  return "business_info" in loc;
}

export function isPerformanceLocation(
  loc: GbpPerformanceLocation | GbpUnreadableLocation,
): loc is GbpPerformanceLocation {
  return "monthly" in loc;
}

/* ── fetchers ──────────────────────────────────────────────────────────── */

/**
 * Resolved per call, not at module scope: getApiBaseUrl throws when the env
 * var is unset, and a module-scope throw would take the whole dashboard import
 * down with it. Called here, a missing config fails only this section.
 */
function socialApiBase(): string {
  return getApiBaseUrl("REACT_APP_SOCIAL_API_URL").replace(/\/$/, "");
}

async function getJson<T>(url: string, idToken: string, what: string): Promise<T> {
  const resp = await authedFetch(url, {}, idToken);
  if (!resp.ok) {
    throw new Error(`${what} API error (${resp.status})`);
  }
  return resp.json();
}

export async function fetchGbpAudit(
  customerId: string,
  idToken: string,
): Promise<GbpAuditResponse> {
  const url = `${socialApiBase()}/${encodeURIComponent(customerId)}/gbp/audit`;
  return getJson<GbpAuditResponse>(url, idToken, "Business Profile audit");
}

/* ── edits: POST /{customer_id}/gbp/edits (SGL-328 Phase C) ────────────── */
//
// The ONE call in the app that changes a customer's Business Profile. It sends a
// single owner-approved edit; social-management's gbp_edits.py validates it,
// writes it, reads it back and reports what Google actually did.

/** Fields Sagelyn can change today (gbp_edits.EDITABLE_FIELDS). */
export type GbpEditField =
  | "services"
  | "description"
  | "website"
  | "phone"
  | "additional_categories"
  | "regular_hours"
  | "opening_date"
  | "primary_category"
  | "attributes";

export type GbpDay = "MONDAY" | "TUESDAY" | "WEDNESDAY" | "THURSDAY" | "FRIDAY" | "SATURDAY" | "SUNDAY";

/** One opening period on one day, "HH:MM" on a 24-hour clock ("24:00" = midnight
 *  at the end of the day). A day with no period is closed. */
export interface GbpHoursPeriod {
  day: GbpDay;
  open: string;
  close: string;
}

/** google.type.Date; the day is optional (Google accepts month and year). */
export interface GbpOpeningDate {
  year: number;
  month: number;
  day?: number;
}

export interface GbpCategoryOption {
  /** Google's resource name, e.g. "categories/gcid:tour_agency". */
  name: string;
  display_name: string;
}

/** One attribute, one value (gbp_edits._attribute_edit). `attribute` is
 *  Google's id ("has_wheelchair_accessible_entrance"). */
export interface GbpAttributeEdit {
  attribute: string;
  value: boolean | string;
}

/** A service with the description the owner approved (SGL-421). Google adds it to
 *  a new service, or to an existing one that has no description yet; it never
 *  replaces a description already on the profile. */
export interface GbpServiceEdit {
  name: string;
  description: string;
}

export type GbpEditValue =
  | string
  | string[]
  | GbpServiceEdit[]
  | GbpHoursPeriod[]
  | GbpOpeningDate
  | GbpAttributeEdit;

/**
 * What Google did with one edit. Never "live" while Google holds it.
 *   applied_unverified — Google accepted the write but the read-back after it
 *                        failed: sent, not confirmed. Never "failed", never "live".
 *   no_answer          — the write may have reached Google but no answer came
 *                        back (read timeout, reset after send). Unknown, not "failed".
 */
export interface GbpEditResult {
  status: "live" | "pending_review" | "applied_unverified" | "no_answer" | "validated" | "failed";
  field: GbpEditField;
  old?: GbpEditValue | null;
  new?: GbpEditValue | null;
  /** The field as Google returned it after the write. */
  now_on_google?: GbpEditValue | null;
  /** Failure code: rejected (Google said no, see message), rate_limited, needs_reauth,
   *  already_set, in_additional_categories, ... */
  reason?: string;
  message?: string;
  /** reason google_updated (SGL-421): the fields Google changed on its own, so nothing was sent. */
  google_changed?: string[];
  /** primary_category only: free-form services the read-back no longer shows. */
  services_no_longer_on_google?: string[];
  /** Whether the applied-edit log recorded it (absent on failed results). */
  logged?: boolean;
}

/** The backend's cap on the buyer questions an edit carries
 *  (social-management gbp_edit_log.validate_questions). */
export const GBP_EDIT_MAX_QUESTIONS = 10;
export const GBP_EDIT_QUESTION_MAX_LENGTH = 300;

/** Questions as the backend accepts them: trimmed, non-empty, at most 300
 *  characters each (longer ones are left out, never cut), at most 10. */
export function editQuestions(questions: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const q of questions ?? []) {
    const s = typeof q === "string" ? q.trim() : "";
    if (s && s.length <= GBP_EDIT_QUESTION_MAX_LENGTH && !out.includes(s)) out.push(s);
    if (out.length === GBP_EDIT_MAX_QUESTIONS) break;
  }
  return out;
}

/**
 * A write (edit or photo) the server answered with a non-2xx status.
 *
 * `refusedBeforeGoogle` is true for a 4xx: every 4xx on these routes (API
 * Gateway auth/throttling, the handler's own validation, "not connected") is
 * returned before any Google call, so nothing was changed. A 5xx is NOT that:
 * a Lambda error or a gateway timeout can come after Google already took the
 * write. A thrown network error (no response at all) is not a GbpWriteError.
 */
export class GbpWriteError extends Error {
  readonly status: number;
  readonly refusedBeforeGoogle: boolean;
  constructor(what: string, status: number) {
    super(`Business Profile ${what} API error (${status})`);
    this.name = "GbpWriteError";
    this.status = status;
    this.refusedBeforeGoogle = status >= 400 && status < 500;
  }
}

export async function applyGbpEdit(
  customerId: string,
  edit: { locationId: string; field: GbpEditField; value: GbpEditValue; questions?: string[] },
): Promise<GbpEditResult> {
  const url = `${socialApiBase()}/${encodeURIComponent(customerId)}/gbp/edits`;
  const questions = editQuestions(edit.questions);
  const resp = await authedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      location_id: edit.locationId,
      field: edit.field,
      value: edit.value,
      // The buyer questions this fix targets, so the next check can measure them (SGL-328 #2).
      ...(questions.length ? { questions } : {}),
    }),
  });
  if (!resp.ok) {
    throw new GbpWriteError("edit", resp.status);
  }
  return resp.json();
}

/** POST /{customer_id}/gbp/google-settings (SGL-421): the owner's word on
 *  Google's automatic updates for one location, kept so later cards, in any
 *  chat, don't ask again. `google`: Google changed the profile itself, so they
 *  are back on (off: false only). No Google call. */
export type GbpSettingsVia = "card" | "chat" | "google";

export async function saveGbpGoogleSettings(
  customerId: string,
  answer: { locationId: string; off: boolean; via: GbpSettingsVia },
): Promise<void> {
  const url = `${socialApiBase()}/${encodeURIComponent(customerId)}/gbp/google-settings`;
  const resp = await authedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ location_id: answer.locationId, off: answer.off, via: answer.via }),
  });
  if (!resp.ok) {
    throw new Error(`Business Profile google-settings API error (${resp.status})`);
  }
}

/** POST /{customer_id}/gbp/edit-status: where each applied change stands on
 *  Google now. Read-only on the server; POST only because the checks are a body. */
export interface GbpEditStatusResponse {
  status: string;
  checked_at?: string;
  results: Record<string, "live" | "waiting" | "not_on_google" | "unknown">;
}

export async function checkGbpEditStatus(
  customerId: string,
  locationId: string,
  checks: Array<Record<string, unknown>>,
): Promise<GbpEditStatusResponse> {
  const url = `${socialApiBase()}/${encodeURIComponent(customerId)}/gbp/edit-status`;
  const resp = await authedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ location_id: locationId, checks }),
  });
  if (!resp.ok) throw new Error(`Business Profile status check failed (${resp.status})`);
  return resp.json();
}

export async function searchGbpCategories(
  customerId: string,
  locationId: string,
  query: string,
): Promise<GbpCategoryOption[]> {
  const url =
    `${socialApiBase()}/${encodeURIComponent(customerId)}/gbp/categories` +
    `?location_id=${encodeURIComponent(locationId)}&q=${encodeURIComponent(query)}`;
  const resp = await authedFetch(url, {});
  if (!resp.ok) {
    throw new Error(`Business Profile category search error (${resp.status})`);
  }
  const data = await resp.json();
  return Array.isArray(data?.categories) ? data.categories : [];
}

/* ── attributes: GET /{customer_id}/gbp/attributes?location_id= ───────── */

export interface GbpAttributeOption {
  /** Google's attribute id, e.g. "wi_fi". */
  id: string;
  display_name: string | null;
  group: string | null;
  /** Only single-value yes/no and one-of-a-list are offered for editing. */
  value_type: "BOOL" | "ENUM";
  options: { value: boolean | string; display_name: string | null }[];
  /** What is set on Google now; null = not set. */
  current: boolean | string | null;
}

export interface GbpAttributesResponse {
  /** "ok", or the token/Google reason (needs_reauth, not_connected, forbidden, ...). */
  status: string;
  attributes: GbpAttributeOption[];
  /** Attributes Google offers that Sagelyn can't set (links, multi-choice). */
  not_editable_count?: number;
  truncated?: boolean;
}

export async function fetchGbpAttributes(
  customerId: string,
  locationId: string,
): Promise<GbpAttributesResponse> {
  const url =
    `${socialApiBase()}/${encodeURIComponent(customerId)}/gbp/attributes` +
    `?location_id=${encodeURIComponent(locationId)}`;
  const resp = await authedFetch(url, {});
  if (!resp.ok) {
    throw new Error(`Business Profile attributes error (${resp.status})`);
  }
  return resp.json();
}

/* ── photos: POST /{customer_id}/gbp/photos ────────────────────────────── */

export { GBP_PHOTO_CATEGORIES };
export type { GbpPhotoCategory };

/** "submitted", never "live": Google gives no review state for a photo. */
export interface GbpPhotoResult {
  status: "submitted" | "no_answer" | "failed";
  field: "photo";
  new?: { source_url: string; category: string } | null;
  media_name?: string | null;
  google_url?: string | null;
  reason?: string;
  message?: string;
  logged?: boolean;
}

export async function uploadGbpPhoto(
  customerId: string,
  photo: { locationId: string; sourceUrl: string; category: GbpPhotoCategory; questions?: string[] },
): Promise<GbpPhotoResult> {
  const url = `${socialApiBase()}/${encodeURIComponent(customerId)}/gbp/photos`;
  const questions = editQuestions(photo.questions);
  const resp = await authedFetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      location_id: photo.locationId,
      source_url: photo.sourceUrl,
      category: photo.category,
      ...(questions.length ? { questions } : {}),
    }),
  });
  if (!resp.ok) {
    throw new GbpWriteError("photo", resp.status);
  }
  return resp.json();
}

export async function fetchGbpPerformance(
  customerId: string,
  idToken: string,
  months = 6,
): Promise<GbpPerformanceResponse> {
  const url =
    `${socialApiBase()}/${encodeURIComponent(customerId)}/gbp/performance` +
    `?months=${encodeURIComponent(String(months))}`;
  return getJson<GbpPerformanceResponse>(url, idToken, "Business Profile performance");
}
